<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import { useLotteryStore } from '../stores/lottery.js'
import { useSellerStore } from '../stores/sellerStore.js'
import { usePaymentStore } from '../stores/paymentStore.js'
import { useAuthStore } from '../stores/auth.js'
import { isLotteryReadOnly, normalizeLotteryStatus } from '../utils/lotteryUi.js'
import { getTicketLifecycleMeta } from '../utils/ticketUi.js'
import { isDataStale } from '../utils/operationsUi.js'
import { canCapturePayment, canRetryPayment, getPaymentStatusMeta, toPaymentOperationalMessage } from '../utils/paymentUi.js'

const route = useRoute()
const router = useRouter()
const lotteryStore = useLotteryStore()
const sellerStore = useSellerStore()
const paymentStore = usePaymentStore()
const authStore = useAuthStore()

const selectedLotteryId = ref('')
const selectedTickets = ref(new Set())
const pendingAction = ref(null)
const pendingActionLoading = ref(false)
const receiptTimestamp = ref('')

const staleData = computed(() => isDataStale(sellerStore.lastSuccessfulRefreshAt, 120000))
const paymentStale = computed(() => isDataStale(paymentStore.lastSuccessfulFetchAt, 120000))

const sellerLotteries = computed(() => {
  return (lotteryStore.lotteries || []).filter((lottery) => {
    const id = Number(lottery?.id ?? lottery?.lottery_id)
    if (!id) return false
    const assignments = lotteryStore.assignments || []
    return assignments.length === 0 || assignments.some((entry) => Number(entry?.user_id) === Number(authStore.userId))
  })
})

const currentLottery = computed(() => lotteryStore.currentLottery)
const currentStatus = computed(() => normalizeLotteryStatus(currentLottery.value))
const readOnlyLottery = computed(() => isLotteryReadOnly(currentLottery.value))
const hasSelection = computed(() => selectedTickets.value.size > 0)

const visibleRows = computed(() => sellerStore.ownedRows)
const paymentByTicket = computed(() => {
  const map = new Map()
  for (const payment of paymentStore.payments) {
    const ticketNumber = Number(payment?.ticketNumber)
    if (!Number.isInteger(ticketNumber) || ticketNumber <= 0) continue
    map.set(ticketNumber, payment)
  }
  return map
})
const selectedPaymentRows = computed(() => {
  return Array.from(selectedTickets.value)
    .map((ticketNumber) => paymentByTicket.value.get(Number(ticketNumber)))
    .filter(Boolean)
})
const sellerTotals = computed(() => {
  return visibleRows.value.reduce((acc, row) => {
    acc.assigned += row.state === 'assigned' ? 1 : 0
    acc.sold += row.state === 'sold' || row.state === 'winner' ? 1 : 0
    acc.reserved += row.state === 'reserved' ? 1 : 0
    return acc
  }, {
    assigned: 0,
    sold: 0,
    reserved: 0,
  })
})

const toggleTicket = (ticketNumber, checked) => {
  const next = new Set(selectedTickets.value)
  if (checked) next.add(Number(ticketNumber))
  else next.delete(Number(ticketNumber))
  selectedTickets.value = next
}

const openAction = (action, kind = 'ticket') => {
  if (!hasSelection.value || readOnlyLottery.value) return
  paymentStore.error = ''

  if (kind === 'payment') {
    const payments = selectedPaymentRows.value
    if (!payments.length) {
      paymentStore.error = 'No payment records are available for the selected tickets yet.'
      return
    }
    if (action === 'capture' && !payments.some((payment) => canCapturePayment(payment))) {
      paymentStore.error = 'Selected tickets do not have capturable payments.'
      return
    }
    if (action === 'retry' && !payments.some((payment) => canRetryPayment(payment))) {
      paymentStore.error = 'Selected tickets do not have retryable payments.'
      return
    }
  }

  pendingAction.value = {
    kind,
    action,
    ticketNumbers: Array.from(selectedTickets.value),
  }
}

const closeAction = () => {
  if (pendingActionLoading.value) return
  pendingAction.value = null
}

const actionTitle = computed(() => {
  const action = pendingAction.value?.action
  if (action === 'reserved') return 'Reserve Tickets'
  if (action === 'sold') return 'Mark Tickets as Sold'
  if (action === 'assigned') return 'Release Reservations'
  if (action === 'capture') return 'Mark Payments as Paid'
  if (action === 'retry') return 'Retry Failed Payments'
  return 'Confirm Seller Operation'
})

const actionTone = computed(() => ['sold', 'capture'].includes(pendingAction.value?.action) ? 'success' : 'warning')

const confirmAction = async () => {
  if (!pendingAction.value || pendingActionLoading.value) return
  pendingActionLoading.value = true
  try {
    if (pendingAction.value.kind === 'payment') {
      const payments = selectedPaymentRows.value
      for (const payment of payments) {
        if (pendingAction.value.action === 'capture' && canCapturePayment(payment)) {
          await paymentStore.capturePayment(payment.id, { lottery_id: selectedLotteryId.value, user_id: authStore.userId })
        }
        if (pendingAction.value.action === 'retry' && canRetryPayment(payment)) {
          await paymentStore.retryPayment(payment.id, { lottery_id: selectedLotteryId.value, user_id: authStore.userId })
        }
      }
      pendingAction.value = null
      receiptTimestamp.value = new Date().toISOString()
      return
    }

    const result = await sellerStore.runSellerOperation({
      lotteryId: selectedLotteryId.value,
      action: pendingAction.value.action,
      ticketNumbers: pendingAction.value.ticketNumbers,
    })

    if (result.success) {
      selectedTickets.value = new Set()
      pendingAction.value = null
      receiptTimestamp.value = new Date().toISOString()
    }
  } finally {
    pendingActionLoading.value = false
  }
}

const loadLottery = async () => {
  if (!selectedLotteryId.value) return
  await Promise.all([
    sellerStore.loadSellerLottery(selectedLotteryId.value),
    paymentStore.fetchPaymentOperations({ lottery_id: selectedLotteryId.value, user_id: authStore.userId }),
  ])
}

onMounted(async () => {
  await lotteryStore.fetchLotteries()
  const routeId = String(route.query.lottery_id || '')
  selectedLotteryId.value = routeId || String(lotteryStore.lotteries?.[0]?.id || '')
  await loadLottery()
})

watch(selectedLotteryId, async (nextId) => {
  if (!nextId) return
  await router.replace({ query: { ...route.query, lottery_id: String(nextId) } })
  selectedTickets.value = new Set()
  await loadLottery()
})

watch(
  () => route.fullPath,
  () => {
    pendingAction.value = null
    pendingActionLoading.value = false
    selectedTickets.value = new Set()
  },
)
</script>

<template>
  <section class="seller-view">
    <header class="header-row">
      <div>
        <h1>Seller Dashboard</h1>
        <p class="muted">Track assigned tickets and register sales safely.</p>
      </div>
      <button class="btn" type="button" :disabled="sellerStore.loading" @click="loadLottery">{{ sellerStore.loading ? 'Refreshing...' : 'Refresh' }}</button>
    </header>

    <p v-if="sellerStore.error" class="error">{{ sellerStore.error }}</p>
    <p v-if="sellerStore.supportReference" class="warning">Need support? Share reference: {{ sellerStore.supportReference }}</p>
    <p v-if="paymentStore.error" class="error">{{ paymentStore.error }}</p>
    <p v-if="paymentStore.operationReference" class="warning">Payment support reference: {{ paymentStore.operationReference }}</p>
    <p v-if="paymentStore.degradedWarning || paymentStore.partialWarning" class="warning">{{ paymentStore.degradedWarning || paymentStore.partialWarning }}</p>
    <p v-if="staleData && !sellerStore.loading" class="warning">Data may be stale. Refresh before confirming new sales.</p>
    <p v-if="paymentStale && !paymentStore.loading" class="warning">Payment data may be stale. Refresh before marking tickets as paid.</p>

    <div class="panel selector-row">
      <label>
        Lottery
        <select v-model="selectedLotteryId" class="form-select" :disabled="sellerStore.loading">
          <option value="">Select lottery</option>
          <option v-for="lottery in sellerLotteries" :key="lottery.id || lottery.lottery_id" :value="String(lottery.id || lottery.lottery_id)">
            {{ lottery.name || `Lottery #${lottery.id || lottery.lottery_id}` }}
          </option>
        </select>
      </label>
      <p class="muted">Status: {{ currentStatus }}</p>
      <p class="muted">Payment records: {{ paymentStore.payments.length }}</p>
    </div>

    <div class="grid">
      <article class="card"><h2>Assigned</h2><p>{{ sellerStore.counters.assigned }}</p></article>
      <article class="card"><h2>Reserved</h2><p>{{ sellerStore.counters.reserved }}</p></article>
      <article class="card"><h2>Sold</h2><p>{{ sellerStore.counters.sold }}</p></article>
      <article class="card"><h2>Remaining</h2><p>{{ sellerStore.counters.remaining }}</p></article>
      <article class="card"><h2>Seller totals</h2><p>{{ sellerTotals.assigned }} / {{ sellerTotals.sold }} / {{ sellerTotals.reserved }}</p></article>
    </div>

    <div class="sticky-actions panel">
      <button class="btn" :disabled="!hasSelection || pendingActionLoading || readOnlyLottery" @click="openAction('reserved')">Reserve</button>
      <button class="btn" :disabled="!hasSelection || pendingActionLoading || readOnlyLottery" @click="openAction('sold')">Mark Sold</button>
      <button class="btn btn-secondary" :disabled="!hasSelection || pendingActionLoading || readOnlyLottery" @click="openAction('assigned')">Release</button>
      <button class="btn" :disabled="!hasSelection || pendingActionLoading || readOnlyLottery || paymentStore.operationLoading" @click="openAction('capture', 'payment')">Mark Paid</button>
      <button class="btn btn-secondary" :disabled="!hasSelection || pendingActionLoading || paymentStore.operationLoading" @click="openAction('retry', 'payment')">Retry Payment</button>
      <span class="muted">Selected: {{ selectedTickets.size }}</span>
    </div>

    <div class="table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th>Select</th>
            <th>Ticket</th>
            <th>State</th>
            <th>Payment</th>
            <th>History</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in visibleRows" :key="row.ticketNumber">
            <td>
              <input
                type="checkbox"
                :disabled="sellerStore.isRowLocked(row.ticketNumber) || pendingActionLoading || readOnlyLottery"
                :checked="selectedTickets.has(row.ticketNumber)"
                @change="toggleTicket(row.ticketNumber, $event.target.checked)"
              />
            </td>
            <td>#{{ row.ticketNumber }}</td>
            <td>
              <span class="chip" :class="`chip-${getTicketLifecycleMeta(row.state).tone}`">{{ getTicketLifecycleMeta(row.state).label }}</span>
            </td>
            <td>
              <template v-if="paymentByTicket.get(row.ticketNumber)">
                <span class="chip" :class="`chip-${getPaymentStatusMeta(paymentByTicket.get(row.ticketNumber)).tone}`">
                  {{ getPaymentStatusMeta(paymentByTicket.get(row.ticketNumber)).label }}
                </span>
                <div class="muted payment-help">{{ toPaymentOperationalMessage(paymentByTicket.get(row.ticketNumber)) }}</div>
              </template>
              <span v-else class="muted">No payment record</span>
            </td>
            <td>{{ row.history?.length || 0 }} event(s)</td>
          </tr>
        </tbody>
      </table>
    </div>

    <section class="panel receipt" v-if="sellerStore.message">
      <h2>Sale Confirmation</h2>
      <p>{{ sellerStore.message }}</p>
      <p class="muted">Confirmed at: {{ receiptTimestamp || sellerStore.lastSuccessfulRefreshAt }}</p>
      <pre class="receipt-code">Scope: seller_operation\nLottery: {{ selectedLotteryId }}\nResult: {{ sellerStore.message }}</pre>
      <p v-if="selectedPaymentRows.length" class="muted">Payment summary: {{ selectedPaymentRows.map((payment) => getPaymentStatusMeta(payment).label).join(', ') }}</p>
    </section>


    <AdminActionDialog
      :open="Boolean(pendingAction)"
      :title="actionTitle"
      :message="`Apply action to ${pendingAction?.ticketNumbers?.length || 0} ticket(s).`"
      :details="['Actions are replay-safe. If connection is unstable, wait before retrying.']"
      :tone="actionTone"
      confirm-label="Confirm"
      :loading="pendingActionLoading"
      loading-label="Applying..."
      @close="closeAction"
      @confirm="confirmAction"
    />
  </section>
</template>

<style scoped>
.seller-view { max-width: 1100px; margin: 0 auto; padding: 1rem; display: grid; gap: 0.8rem; }
.header-row { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; }
.selector-row { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: end; }
.grid { display: grid; gap: 0.75rem; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
.card, .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 0.75rem; }
.table-wrap { overflow: auto; }
.table { width: 100%; border-collapse: collapse; }
.table th, .table td { border-bottom: 1px solid #e5e7eb; padding: 0.5rem; text-align: left; }
.table thead th { position: sticky; top: 0; background: #f9fafb; }
.sticky-actions { position: sticky; top: 58px; z-index: 2; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
.chip { border-radius: 999px; padding: 0.2rem 0.5rem; border: 1px solid transparent; font-size: 0.8rem; }
.chip-neutral { background: #f3f4f6; color: #374151; border-color: #d1d5db; }
.chip-warning { background: #fffbeb; color: #92400e; border-color: #fcd34d; }
.chip-success { background: #dcfce7; color: #166534; border-color: #86efac; }
.chip-active { background: #dbeafe; color: #1d4ed8; border-color: #93c5fd; }
.chip-danger { background: #fee2e2; color: #991b1b; border-color: #fca5a5; }
.payment-help { margin-top: 0.25rem; font-size: 0.8rem; }
.receipt { border-left: 4px solid #2563eb; }
.receipt-code { white-space: pre-wrap; font-size: 0.85rem; background: #f9fafb; border: 1px dashed #d1d5db; padding: 0.5rem; border-radius: 6px; }
.btn { border: none; border-radius: 6px; background: #2563eb; color: #fff; padding: 0.45rem 0.75rem; }
.btn-secondary { background: #4b5563; }
.form-select { border: 1px solid #d1d5db; border-radius: 6px; min-height: 40px; padding: 0.4rem; }
.muted { color: #6b7280; }
.warning { color: #92400e; background: #fffbeb; border-radius: 6px; padding: 0.45rem; }
.error { color: #991b1b; background: #fee2e2; border-radius: 6px; padding: 0.45rem; }
@media (max-width: 768px) {
  .sticky-actions { position: sticky; bottom: 0; top: auto; background: #fff; }
  .sticky-actions .btn { flex: 1; min-height: 44px; }
}
</style>

